/**
 * Invoice Status Constants and Utilities
 *
 * This file defines the valid invoice statuses and their display properties.
 * These MUST match the STATUS_CHOICES in the backend (api/accounts/models.py).
 *
 * Backend statuses: draft, awaiting_payment, paid, bad_debt
 * Note: "Overdue" is displayed as a tag when awaiting_payment and due_date < today
 */

export const INVOICE_STATUSES = ['draft', 'awaiting_payment', 'paid', 'bad_debt'] as const;

export type InvoiceStatus = typeof INVOICE_STATUSES[number];

export interface InvoiceStatusConfig {
  label: string;
  textColorClass: string;
}

/**
 * Status display configuration
 * - draft: muted gray
 * - awaiting_payment: blue
 * - paid: green success
 * - bad_debt: red error
 */
export const INVOICE_STATUS_CONFIG: Record<InvoiceStatus, InvoiceStatusConfig> = {
  draft: {
    label: 'Draft',
    textColorClass: 'text-muted-foreground',
  },
  awaiting_payment: {
    label: 'Awaiting Payment',
    textColorClass: 'text-blue-600 dark:text-blue-400',
  },
  paid: {
    label: 'Paid',
    textColorClass: 'text-green-600 dark:text-green-400',
  },
  bad_debt: {
    label: 'Bad Debt',
    textColorClass: 'text-red-600 dark:text-red-400',
  },
};

/**
 * Checks if an invoice is overdue (past due date).
 * Returns true if status is awaiting_payment and due_date is before today.
 */
export function isInvoiceOverdue(invoice: { status: string; due_date: string | null }): boolean {
  if (invoice.status !== 'awaiting_payment' || !invoice.due_date) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  // Parse date as local time (not UTC) to avoid timezone issues
  const [year, month, day] = invoice.due_date.split('-').map(Number);
  const dueDate = new Date(year, month - 1, day);
  return dueDate < today;
}

/**
 * Validates that a status string is a valid invoice status.
 * Throws a descriptive error if the status is invalid.
 *
 * @param status - The status string to validate
 * @param context - Optional context for the error message (e.g., "Invoice #123")
 * @returns The validated status as InvoiceStatus type
 * @throws Error if status is not valid
 */
export function validateInvoiceStatus(status: string, context?: string): InvoiceStatus {
  if (!INVOICE_STATUSES.includes(status as InvoiceStatus)) {
    const validStatuses = INVOICE_STATUSES.join(', ');
    const contextStr = context ? ` for ${context}` : '';
    throw new Error(
      `Invalid invoice status "${status}"${contextStr}. ` +
      `Valid statuses are: ${validStatuses}. ` +
      `This may indicate a mismatch between frontend and backend status definitions. ` +
      `Please ensure frontend (src/lib/invoiceStatus.ts) matches backend (api/accounts/models.py STATUS_CHOICES).`
    );
  }
  return status as InvoiceStatus;
}

/**
 * Gets the display configuration for an invoice status.
 * Validates the status and throws a descriptive error if invalid.
 *
 * @param status - The status string
 * @param context - Optional context for error messages
 * @returns The status configuration with label and style
 */
export function getInvoiceStatusConfig(status: string, context?: string): InvoiceStatusConfig {
  const validatedStatus = validateInvoiceStatus(status, context);
  return INVOICE_STATUS_CONFIG[validatedStatus];
}

/**
 * Checks if a status is valid without throwing.
 * Useful for conditional rendering or filtering.
 *
 * @param status - The status string to check
 * @returns true if the status is valid
 */
export function isValidInvoiceStatus(status: string): status is InvoiceStatus {
  return INVOICE_STATUSES.includes(status as InvoiceStatus);
}
